import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/Menu';

// Matches the actions wrapper only when a page has actually put something in it.
// The wrapper always exists, and in the embertracking app its child is a portal
// target that is present-but-empty on most pages — so "has a non-empty child" is
// the question, not "has a child".
const ACTIONS_FILLED = '.et-header-actions > :not(:empty)';

// The collapsed search field is a 42px circle, and it is the tallest thing in
// this row — so below md the row's height IS the field's, on every page that
// shows one. Reserve it, or the bar visibly resizes the moment the field stands
// down for a ~20px status line and back again when the form is saved.
const MOBILE_ROW_HEIGHT = 42;

// Height a page asks for with `statusReserved`, from when the status line sat on
// its own row below the breadcrumb. It no longer does — it shares row one now —
// so this only still matters as a floor above MOBILE_ROW_HEIGHT. Kept because
// Check and Pretrip pass the prop and this is what they've always been sized to.
const RESERVED_ROW_HEIGHT = 52;

// Shared page header bar: an optional menu toggle, a breadcrumbs slot (children)
// and a right-aligned actions slot. Styling (paper background, bottom divider,
// responsive padding) is shared across the embertracking and hub apps.
export default function Header({
    onToggleMenu,
    toggleTitle,
    toggleSx,
    actions,
    // Right-aligned slot ahead of `actions`, for the global ⌘K field (#1715).
    // Separate from `actions` because that slot is per-page (portalled into by
    // HeaderSlotTarget) while this one belongs to the shell and is on every page.
    search,
    statusReserved = false,
    hideWhenEmpty = false,
    sx,
    children,
    ...rest
}) {
    return (
        <Box
            {...rest}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                // iOS Safari renders the page up behind the status bar; give the
                // header a defined top edge there only (iOS-only support query).
                '@supports (-webkit-touch-callout: none)': {
                    borderTop: 1,
                    borderTopColor: 'divider'
                },
                px: {xs: 1, md: 3},
                py: {xs: 1.5, md: 3},
                // Collapse the whole bar when there's nothing to show (no
                // breadcrumb trail and no dashboard greeting). The global search
                // field counts as something to show — it's the shell's own
                // control and has to stay reachable on pages that render neither.
                ...(hideWhenEmpty && {
                    '&:not(:has(.MuiBreadcrumbs-root, [data-testid="dashboard-greeting"], [data-testid="global-search"]))': {display: 'none'}
                }),
                ...sx
            }}
        >
            <Box sx={{display: 'flex', alignItems: 'center', gap: {xs: 1, md: 2}, minWidth: 0}}>
                {onToggleMenu && (
                    <Tooltip title={toggleTitle || ''}>
                        <IconButton
                            onClick={onToggleMenu}
                            sx={{flexShrink: 0, p: 1, ...toggleSx}}
                            data-testid="toggle-sidebar"
                        >
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>
                )}
                <Box
                    sx={theme => ({
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        // One row at every width: the trail (or greeting) takes
                        // what it needs and the search field sits hard right.
                        // This used to stack into a column below md, which spent
                        // a whole line on a lone 42px magnifier and left the
                        // right half of the bar empty on every mobile page.
                        // Only `actions` wraps — see its flexBasis below.
                        flexWrap: 'wrap',
                        columnGap: 2,
                        rowGap: 0.5,
                        [theme.breakpoints.down('md')]: {
                            // One height for the row whatever is in it — field,
                            // status line, or both swapping over — so nothing
                            // below the header moves when they trade places.
                            minHeight: statusReserved ? RESERVED_ROW_HEIGHT : MOBILE_ROW_HEIGHT,
                            // Page status and the search field both want the right
                            // end of a phone header and only one can have it.
                            // Giving them a row each made a two-line bar out of
                            // every dirty form; the status line is the one tied to
                            // what you're doing right now, so the field stands down
                            // while it's up. Note this puts search out of reach on a
                            // dirty form until it's saved or discarded — deliberate,
                            // and the reason it's scoped below md, where the two
                            // genuinely can't share.
                            [`&:has(${ACTIONS_FILLED}) [data-testid="global-search"]`]: {display: 'none'},
                            // An open search field owns the mobile row outright.
                            // There is no width at which a trail and a usable field
                            // both fit on a phone, and the trail is the one you can
                            // get back by closing the field. Guarded on the field
                            // actually being present, so a ⌘K from a hardware
                            // keyboard can't hide the trail for a field that the
                            // rule above has already taken away.
                            [`&:not(:has(${ACTIONS_FILLED})):has([data-search-expanded="true"]) .et-header-title`]: {display: 'none'}
                        }
                    })}
                >
                    {/* `1 1 0` and not `1 1 auto`: a wrapping flex container
                        breaks lines on each item's CONTENT width before it ever
                        shrinks one, so an auto basis let a long dashboard
                        greeting push the search field onto a line of its own.
                        A zero basis means the title can never force the break —
                        it takes the space the field leaves and wraps inside it. */}
                    <Box className="et-header-title" sx={{flex: '1 1 0', minWidth: 0, maxWidth: '100%'}}>
                        {children}
                    </Box>
                    {search}
                    {/* Page actions are mostly status text ("No unsaved changes",
                        "Check resolved by …"). With a search field present they
                        share row one, because the rule above stands the field down
                        to make room. Without one (the hub, or a station login) there
                        is nothing to stand down, so they take a row of their own
                        rather than squeeze the trail to an ellipsis — the trail's
                        zero basis means it yields width instead of forcing a break.
                        The two empty guards are what stop a row being reserved for
                        nothing — `:empty` for an app that passes no actions at all
                        (the hub, for a non-admin), `:has(> :empty)` for one that
                        passes a portal target no page has filled. */}
                    <Box
                        className="et-header-actions"
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            // Right-aligned for the own-row case above, so its
                            // contents end on the same gutter as everything else
                            // rather than orphaned at the left margin. A no-op
                            // wherever the box is only as wide as what's in it.
                            justifyContent: 'flex-end',
                            minWidth: 0,
                            maxWidth: '100%',
                            flexBasis: {xs: search ? 'auto' : '100%', md: 'auto'},
                            '&:empty, &:has(> :empty)': {display: 'none'}
                        }}
                    >
                        {actions}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
